import React from "react"
import { connect } from "react-redux"
import { AddFiles } from "./AddFiles/AddFiles"
import { DataMapper } from "./DataMapper/DataMapper"
import { validate } from "../../../Helpers/Schema/Schema"
import { isConsistentJSONArray } from "../../../Helpers/Functions/validate"
import {
    isValidObject,
    getObjectKeysSafely
} from "../../../Helpers/Functions/object"
import { State, MainThunkDispatch } from "../../../Reducers"
import {
    setDataMapper,
    clearDataMapper
} from "../../../Actions/Client/DataFileExplorer/DataMapperActions"
import {
    addDataToFileExplorer,
    clearDataFileExplorer
} from "../../../Actions/Client/DataFileExplorer/AddDataActions"
import { Button, Box } from "dash-styled"
import { JSONSchema7 } from "json-schema"

interface DataFileExplorerProps {
    id: string
    schema: JSONSchema7
}

interface StateProps {
    map?: {
        schema: any
        data: any[]
    }
    dataExists: boolean
}

interface DispatchProps {
    addData: (data: any[]) => void
    clearData: () => void
    mapData: (schema: any, data: any[]) => void
    clearMap: () => void
}

export type Props = DataFileExplorerProps & StateProps & DispatchProps

export const DataFileExplorerIsolated = ({
    id,
    schema,
    addData: add,
    map,
    mapData,
    clearMap,
    clearData,
    dataExists
}: Props) => {
    // Because the validator doesn't filter
    const addData = (data: any[]) => {
        const keys = getObjectKeysSafely(schema.properties) || []
        const d = data.map(item =>
            keys.reduce(
                (passed, key) => ({
                    ...passed,
                    [key]: item[key]
                }),
                {}
            )
        )
        add(d)
    }

    const handleFile = (fileContents: string) => {
        try {
            const json = JSON.parse(fileContents)
            if (Array.isArray(json)) {
                // ensure consistency
                if (!isConsistentJSONArray(json))
                    throw Error("JSON Array is not consistent")
                // check if item 0 matches schema, add
                if (validate(schema, json[0]).valid) {
                    addData(json)
                } // else, show column map
                else {
                    mapData(schema, json)
                }
            } else if (isValidObject(json)) {
                // if matches schema, add
                if (validate(schema, json).valid) {
                    addData([json])
                } // else show column map
                else {
                    mapData(schema, [json])
                }
            } else {
                throw Error("JSON is not of type Object or Array")
            }
        } catch (error) {
            alert(error || "Invalid JSON")
        }
    }

    const onMapped = (data: any[]) => {
        addData(data)
        clearMap()
    }
    const clearButton = dataExists ? (
        <Button color="red" onClick={clearData} variant="contained">
            Clear data
        </Button>
    ) : null

    return (
        <Box marginBottom="4">
            <Box marginBottom="3">
                <AddFiles onFileReceived={handleFile} />
            </Box>
            {clearButton}
            {map && <DataMapper {...map} onMapped={onMapped} id={id} />}
        </Box>
    )
}

const mapStateToProps = (
    state: State,
    { id }: DataFileExplorerProps
): StateProps => {
    const explorerEntry = state.client.dataFileExplorer.explorer[id]

    return {
        map: state.client.dataFileExplorer.columnMap[id],
        dataExists:
            explorerEntry === undefined ? false : explorerEntry.length > 0
    }
}

const mapDispatchToProps = (
    dispatch: MainThunkDispatch,
    { id }: DataFileExplorerProps
): DispatchProps => {
    return {
        addData: (data: any[]) => dispatch(addDataToFileExplorer(id, data)),
        clearData: () => dispatch(clearDataFileExplorer(id)),
        mapData: (schema: any, data: any[]) =>
            dispatch(setDataMapper(id, schema, data)),
        clearMap: () => dispatch(clearDataMapper(id))
    }
}

export const DataFileExplorer = connect(
    mapStateToProps,
    mapDispatchToProps
)(DataFileExplorerIsolated)
